import { useMany, useTranslate } from '@refinedev/core';
import { Card, Table, Typography } from 'antd';
import type {
  PublisherListOut,
  RoyaltyReportDetailOut,
  RoyaltyStatementOut,
} from '@/api/generated/schemas';
import { PublisherIconLink } from '@/components/user_catalog/publishers/PublisherIconLink';
import { formatDateTime, formatUnits } from '@/utils/format';
import { formatLedger } from '@/utils/money';
import { HintLabel } from '@/components/common/HintLabel';
import { PayableAmount } from '@/components/settlement/PayableAmount';
import { NUMERIC_STYLE } from '@/components/settlement/numeric';
import { currentStatements, statementHistory } from '../statements';

const T = 'settlement.royalty_reports';

/**
 * The group's shares: one row per publisher (the parent and its imprints)
 * at its current version. Statements are append-only — a changed share adds
 * a version — so the superseded ones stay one expand away. A click opens the
 * breakdown the version was computed from.
 */
export const RoyaltyReportStatementsCard = ({
  record,
  onOpenBreakdown,
}: {
  record: RoyaltyReportDetailOut;
  onOpenBreakdown: (statementId: number) => void;
}) => {
  const t = useTranslate();
  const current = currentStatements(record.statements);

  const publisherIds = current.map((s) => s.publisher_id);
  const { result: publishersResult } = useMany<PublisherListOut>({
    resource: 'user_catalog.publishers',
    ids: publisherIds,
    queryOptions: { enabled: publisherIds.length > 0 },
  });
  const publisherMap = new Map<number, PublisherListOut>(
    (publishersResult?.data ?? []).map((p: PublisherListOut) => [p.id, p])
  );

  const onRow = (row: RoyaltyStatementOut) => ({
    onClick: () => onOpenBreakdown(row.id),
    style: { cursor: 'pointer' },
  });

  const versionCell = (value: number) => (
    <Typography.Text type="secondary">{`v${value}`}</Typography.Text>
  );
  const ledgerCell = (value: string, row: RoyaltyStatementOut) => (
    <span style={NUMERIC_STYLE}>{formatLedger(value, row.currency)}</span>
  );
  const payableCell = (value: string, row: RoyaltyStatementOut) => (
    <PayableAmount
      amount={value}
      roundingDelta={row.rounding_delta}
      currency={row.currency}
    />
  );

  return (
    <Card title={t(`${T}.sections.statements`)}>
      <Table<RoyaltyStatementOut>
        dataSource={current}
        size="small"
        pagination={false}
        rowKey="id"
        onRow={onRow}
        scroll={{ x: 'max-content' }}
        expandable={{
          rowExpandable: (row) =>
            statementHistory(record.statements, row).length > 0,
          expandedRowRender: (row) => (
            <Table<RoyaltyStatementOut>
              dataSource={statementHistory(record.statements, row)}
              size="small"
              pagination={false}
              rowKey="id"
              onRow={onRow}
              showHeader={false}
              title={() => t(`${T}.statements.history`)}
              scroll={{ x: 'max-content' }}
            >
              <Table.Column
                dataIndex="version"
                width={60}
                render={versionCell}
              />
              <Table.Column
                dataIndex="created_at"
                render={(value?: string | null) => formatDateTime(value) ?? '—'}
              />
              <Table.Column
                dataIndex="units_count"
                align="right"
                render={(value: string) => (
                  <span style={NUMERIC_STYLE}>{formatUnits(value)}</span>
                )}
              />
              <Table.Column
                dataIndex="amount"
                align="right"
                render={ledgerCell}
              />
              <Table.Column
                dataIndex="payable_amount"
                align="right"
                render={payableCell}
              />
            </Table>
          ),
        }}
      >
        <Table.Column
          dataIndex="publisher_id"
          title={t(`${T}.fields.publisher_id`)}
          render={(value: number) => (
            // Stop the row click: the link has its own target.
            <span onClick={(e) => e.stopPropagation()}>
              <PublisherIconLink
                publisherId={value}
                publisher={publisherMap.get(value)}
              />
            </span>
          )}
        />
        <Table.Column
          dataIndex="version"
          title={t(`${T}.fields.version`)}
          width={60}
          render={versionCell}
        />
        <Table.Column
          dataIndex="units_count"
          title={t(`${T}.fields.units_count`)}
          align="right"
          render={(value: string) => (
            <span style={NUMERIC_STYLE}>{formatUnits(value)}</span>
          )}
        />
        <Table.Column
          dataIndex="amount"
          title={
            <HintLabel
              label={t(`${T}.fields.amount_ledger`)}
              hint={t('settlement.hints.amount_ledger')}
            />
          }
          align="right"
          render={ledgerCell}
        />
        <Table.Column
          dataIndex="payable_amount"
          title={
            <HintLabel
              label={t(`${T}.fields.payable_amount`)}
              hint={t('settlement.hints.payable_amount')}
            />
          }
          align="right"
          render={payableCell}
        />
      </Table>
    </Card>
  );
};
